import React from 'react';
import { Box, Container, Typography, Button, Stack } from '@mui/material';
import { alpha } from '@mui/material/styles';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import { useNavigate } from 'react-router-dom';
import { PREMIUM_COLORS, tokens } from '../theme';
import { VisitioWordmark } from '../components/auth/AuthLayout';

const sb = PREMIUM_COLORS.sidebar;

const FEATURES = [
  { icon: <EventAvailableOutlinedIcon />, title: 'Rezerwacje online', text: 'Klienci umawiają wizyty przez wizytówkę salonu, o każdej porze.' },
  { icon: <PeopleAltOutlinedIcon />, title: 'Baza klientów', text: 'Klienci salonu i historia ich wizyt w jednym miejscu.' },
  { icon: <PaymentsOutlinedIcon />, title: 'Płatności BLIK', text: 'Płatność online za wizytę kartą lub BLIK-iem.' },
  { icon: <SmsOutlinedIcon />, title: 'Powiadomienia SMS', text: 'Wiadomości do klientów w sprawie ich wizyt.' },
];

export const HomePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: tokens.ground, display: 'flex', flexDirection: 'column' }}>
      <Box
        component="header"
        sx={{
          bgcolor: tokens.aubergine,
          // Ledwie widoczna poswiata jak w pasku bocznym aplikacji.
          backgroundImage: `radial-gradient(90% 60% at 0% 0%, ${alpha(tokens.champagne, 0.1)}, transparent 60%)`,
          color: sb.text,
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} sx={{ py: { xs: 2.5, md: 3.5 } }}>
            <VisitioWordmark tone="dark" />
            <Button
              variant="text"
              onClick={() => navigate('/login')}
              sx={{ color: tokens.paper, '&:hover': { bgcolor: alpha(tokens.paper, 0.06) } }}
            >
              Zaloguj się
            </Button>
          </Stack>

          <Box sx={{ pt: { xs: 7, md: 12 }, pb: { xs: 8, md: 14 }, maxWidth: 720 }}>
            <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 2.5 }}>
              <Box sx={{ width: 32, height: '1px', bgcolor: alpha(tokens.champagne, 0.7) }} />
              <Typography variant="overline" component="p" sx={{ color: tokens.champagne, lineHeight: 1.5 }}>
                System dla salonów beauty
              </Typography>
            </Stack>
            <Typography
              variant="h1"
              sx={{ color: tokens.paper, fontSize: { xs: '2.25rem', md: '3.25rem' }, lineHeight: 1.12, textWrap: 'balance' } as object}
            >
              Zarządzanie salonem beauty w jednym miejscu
            </Typography>
            <Typography sx={{ mt: 2.5, fontSize: { xs: '1rem', md: '1.125rem' }, lineHeight: 1.65, color: alpha(tokens.paper, 0.78), maxWidth: 560 }}>
              Rezerwacje online, baza klientów, płatności BLIK i powiadomienia SMS. Prowadź salon sprawnie i profesjonalnie.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} sx={{ mt: 4.5 }}>
              <Button variant="contained" size="large" onClick={() => navigate('/login')}>
                Zaloguj się
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/register')}
                sx={{
                  bgcolor: 'transparent',
                  color: tokens.paper,
                  borderColor: alpha(tokens.paper, 0.28),
                  '&:hover': { bgcolor: alpha(tokens.paper, 0.06), borderColor: alpha(tokens.paper, 0.5) },
                }}
              >
                Załóż konto
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" component="main" sx={{ py: { xs: 6, md: 9 }, flex: 1 }}>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
          }}
        >
          {FEATURES.map((f) => (
            <Box key={f.title} sx={{ bgcolor: tokens.paper, border: `1px solid ${tokens.line}`, borderRadius: '14px', p: 2.5, height: '100%' }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '11px',
                  bgcolor: tokens.plumSoft,
                  color: tokens.plum,
                  display: 'grid',
                  placeItems: 'center',
                  mb: 2,
                  '& .MuiSvgIcon-root': { fontSize: 22 },
                }}
              >
                {f.icon}
              </Box>
              <Typography variant="h5" component="h2">{f.title}</Typography>
              <Typography sx={{ mt: 0.75, color: tokens.inkSoft, fontSize: '0.9375rem' }}>{f.text}</Typography>
            </Box>
          ))}
        </Box>
      </Container>

      <Box component="footer" sx={{ borderTop: `1px solid ${tokens.line}` }}>
        <Container maxWidth="lg">
          <Typography variant="caption" component="div" sx={{ py: 3 }}>
            © {new Date().getFullYear()} Visitio · System dla salonów beauty
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};
